import { useState, useEffect, useCallback } from "react";
import {
  Wrench, Search, Calendar, Clock, User, Car, Phone,
  CheckCircle, AlertCircle, Loader2, MoreVertical,
  MessageCircle, ExternalLink, Edit, Trash2, Play, ChevronLeft, ChevronRight,
  Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { authFetch } from "../services/authService";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  scheduled: { label: "Programmato", color: "bg-blue-500", textColor: "text-blue-400" },
  in_progress: { label: "In Corso", color: "bg-yellow-500", textColor: "text-yellow-400" },
  completed: { label: "Completato", color: "bg-green-500", textColor: "text-green-400" },
  cancelled: { label: "Annullato", color: "bg-red-500", textColor: "text-red-400" },
};

const URGENCY_CONFIG = {
  low: { label: "Bassa (>8gg)", color: "text-green-400", bg: "bg-green-500/20" },
  medium: { label: "Media (≤7gg)", color: "text-yellow-400", bg: "bg-yellow-500/20" },
  high: { label: "Alta (≤3gg)", color: "text-red-400", bg: "bg-red-500/20" },
};

export default function WorkOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [settings, setSettings] = useState({ operators: [], num_bridges: 1 });

  // Dialog states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  // Reschedule form
  const [rescheduleData, setRescheduleData] = useState({
    scheduled_date: "",
    scheduled_time_start: "",
    scheduled_time_end: ""
  });

  // Assign form
  const [assignData, setAssignData] = useState({
    operator_id: "",
    bridge_number: 1
  });

  const fetchOrders = useCallback(async () => {
    try {
      let url = `${API}/work-orders`;
      if (statusFilter !== "all") {
        url += `?status=${statusFilter}`;
      }
      const res = await authFetch(url);
      if (res.ok) {
        const data = await res.json();
        setOrders(data);
      }
    } catch (error) {
      console.error("Error fetching work orders:", error);
      toast.error("Errore nel caricamento degli ordini");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/officina/settings`, { });
      if (res.ok) {
        const data = await res.json();
        setSettings({
          operators: data.operators || [],
          num_bridges: data.num_bridges || 1
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    fetchSettings();
  }, [fetchOrders, fetchSettings]);

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchSearch = 
      order.work_order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.vehicle_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.operator_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchSearch;
  });

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const res = await authFetch(`${API}/work-orders/${orderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        toast.success(`Stato aggiornato: ${STATUS_CONFIG[newStatus]?.label}`);
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Reschedule order
  const handleReschedule = async () => {
    if (!selectedOrder) return;

    try {
      const res = await authFetch(`${API}/work-orders/${selectedOrder.work_order_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(rescheduleData)
      });

      if (res.ok) {
        toast.success("Ordine riprogrammato");
        setShowRescheduleDialog(false);
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nella riprogrammazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Assign operator/bridge
  const handleAssign = async () => {
    if (!selectedOrder) return;

    const operatorName = settings.operators.find(op => op.id === assignData.operator_id)?.name || "";

    try {
      const res = await authFetch(`${API}/work-orders/${selectedOrder.work_order_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          operator_id: assignData.operator_id,
          operator_name: operatorName,
          bridge_number: assignData.bridge_number
        })
      });

      if (res.ok) {
        toast.success("Assegnazione aggiornata");
        setShowAssignDialog(false);
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nell'assegnazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Delete order
  const deleteOrder = async (orderId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo ordine di lavoro?")) return;

    try {
      const res = await authFetch(`${API}/work-orders/${orderId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        toast.success("Ordine eliminato");
        fetchOrders();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  // Send WhatsApp to operator
  const sendWhatsAppToOperator = (order) => {
    const operator = settings.operators.find(op => op.id === order.operator_id);
    if (!operator?.phone) {
      toast.error("Numero di telefono dell'operatore non disponibile");
      return;
    }

    const completionUrl = `${window.location.origin}/work-complete/${order.completion_token}`;
    
    const message = `🔧 *ORDINE DI LAVORO #${order.work_order_number}*

📅 Data: ${formatDate(order.scheduled_date)}
🕐 Orario: ${order.scheduled_time_start} - ${order.scheduled_time_end}

🚗 Veicolo: ${order.vehicle_brand} ${order.vehicle_model}
🔢 Targa: ${order.vehicle_plate}
👤 Cliente: ${order.customer_name}

⏱️ Ore manodopera: ${order.labor_hours?.toFixed(1) || "N/D"}h
🔧 Ponte: #${order.bridge_number}

📋 Lavori da eseguire:
${order.work_description?.join("\n") || "Vedi preventivo"}

✅ *Conferma fine lavoro:*
${completionUrl}`;

    const phone = operator.phone.replace(/\D/g, "");
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  // Open completion link
  const openCompletionLink = (order) => {
    const url = `${window.location.origin}/work-complete/${order.completion_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiato negli appunti");
  };

  // Download PDF
  const downloadPdf = async (order) => {
    try {
      const res = await authFetch(`${API}/work-orders/${order.work_order_id}/pdf`, {
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `OrdLavoro_${order.work_order_number.replace('/', '-')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("PDF scaricato");
      } else {
        toast.error("Errore nel download del PDF");
      }
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Errore di connessione");
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/D";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" });
  };

  // KPI counts
  const kpiCounts = {
    scheduled: orders.filter(o => o.status === "scheduled").length,
    in_progress: orders.filter(o => o.status === "in_progress").length,
    completed: orders.filter(o => o.status === "completed").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Wrench className="w-7 h-7 text-primary" />
            Ordini di Lavoro
          </h1>
          <p className="text-zinc-400 mt-1">
            Gestisci gli ordini di lavoro creati dai preventivi confermati
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 rounded-full bg-blue-500/20">
            <Calendar className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{kpiCounts.scheduled}</p>
            <p className="text-sm text-zinc-400">Programmati</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 rounded-full bg-yellow-500/20">
            <Play className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{kpiCounts.in_progress}</p>
            <p className="text-sm text-zinc-400">In Corso</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4">
          <div className="p-3 rounded-full bg-green-500/20">
            <CheckCircle className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <p className="text-2xl font-bold text-white">{kpiCounts.completed}</p>
            <p className="text-sm text-zinc-400">Completati</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            type="text"
            placeholder="Cerca per numero, cliente, targa, operatore..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-work-orders"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="filter-status">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="scheduled">Programmati</SelectItem>
            <SelectItem value="in_progress">In Corso</SelectItem>
            <SelectItem value="completed">Completati</SelectItem>
            <SelectItem value="cancelled">Annullati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="card p-12 text-center">
          <Wrench className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Nessun ordine di lavoro trovato</p>
          <p className="text-zinc-500 text-sm mt-1">
            Gli ordini vengono creati automaticamente quando confermi un preventivo
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.scheduled;
            const urgency = URGENCY_CONFIG[order.urgency] || URGENCY_CONFIG.medium;

            return (
              <div key={order.work_order_id} className="card p-4 hover:bg-zinc-800/50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Status indicator */}
                    <div className={`w-1 h-full min-h-[80px] rounded-full ${status.color}`} />
                    
                    <div>
                      {/* Header */}
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-white font-bold">#{order.work_order_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${urgency.bg} ${urgency.color}`}>
                          {urgency.label}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded bg-zinc-700 ${status.textColor}`}>
                          {status.label}
                        </span>
                      </div>

                      {/* Customer & Vehicle */}
                      <div className="flex items-center gap-4 text-sm mb-2">
                        <span className="text-zinc-300 flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {order.customer_name}
                        </span>
                        <span className="text-zinc-400 flex items-center gap-1">
                          <Car className="w-4 h-4" />
                          {order.vehicle_brand} {order.vehicle_model} - {order.vehicle_plate}
                        </span>
                      </div>

                      {/* Schedule */}
                      <div className="flex items-center gap-4 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(order.scheduled_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {order.scheduled_time_start} - {order.scheduled_time_end}
                        </span>
                        <span className="text-primary">
                          Ponte #{order.bridge_number}
                        </span>
                        {order.operator_name && (
                          <span className="text-zinc-300">
                            Op: {order.operator_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {order.operator_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => sendWhatsAppToOperator(order)}
                        className="text-green-400 border-green-500/50 hover:bg-green-500/10"
                        title="Invia su WhatsApp"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-800 border-zinc-700">
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowDetailDialog(true);
                          }}
                          className="text-zinc-300 hover:bg-zinc-700"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Dettagli
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => downloadPdf(order)}
                          className="text-zinc-300 hover:bg-zinc-700"
                          data-testid={`download-pdf-${order.work_order_id}`}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Scarica PDF
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedOrder(order);
                            setRescheduleData({
                              scheduled_date: order.scheduled_date,
                              scheduled_time_start: order.scheduled_time_start,
                              scheduled_time_end: order.scheduled_time_end
                            });
                            setShowRescheduleDialog(true);
                          }}
                          className="text-zinc-300 hover:bg-zinc-700"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Riprogramma
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setSelectedOrder(order);
                            setAssignData({
                              operator_id: order.operator_id || "",
                              bridge_number: order.bridge_number || 1
                            });
                            setShowAssignDialog(true);
                          }}
                          className="text-zinc-300 hover:bg-zinc-700"
                        >
                          <User className="w-4 h-4 mr-2" />
                          Assegna
                        </DropdownMenuItem>
                        {order.status === "scheduled" && (
                          <DropdownMenuItem
                            onClick={() => updateOrderStatus(order.work_order_id, "in_progress")}
                            className="text-yellow-400 hover:bg-zinc-700"
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Inizia Lavoro
                          </DropdownMenuItem>
                        )}
                        {order.status === "in_progress" && (
                          <DropdownMenuItem
                            onClick={() => updateOrderStatus(order.work_order_id, "completed")}
                            className="text-green-400 hover:bg-zinc-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Completa
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => openCompletionLink(order)}
                          className="text-zinc-300 hover:bg-zinc-700"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Copia Link Conferma
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => deleteOrder(order.work_order_id)}
                          className="text-red-400 hover:bg-zinc-700"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              Ordine #{selectedOrder?.work_order_number}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Cliente</p>
                  <p className="text-white">{selectedOrder.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Telefono</p>
                  <p className="text-white">{selectedOrder.customer_phone || "N/D"}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Veicolo</p>
                  <p className="text-white">{selectedOrder.vehicle_brand} {selectedOrder.vehicle_model}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Targa</p>
                  <p className="text-white">{selectedOrder.vehicle_plate}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Data</p>
                  <p className="text-white">{formatDate(selectedOrder.scheduled_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Orario</p>
                  <p className="text-white">{selectedOrder.scheduled_time_start} - {selectedOrder.scheduled_time_end}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Ore Manodopera</p>
                  <p className="text-white">{selectedOrder.labor_hours?.toFixed(1) || "N/D"} h</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Ponte</p>
                  <p className="text-white">#{selectedOrder.bridge_number}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Operatore</p>
                  <p className="text-white">{selectedOrder.operator_name || "Non assegnato"}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase">Preventivo</p>
                  <p className="text-white">#{selectedOrder.quote_number}</p>
                </div>
              </div>

              {selectedOrder.work_description?.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-2">Lavori da Eseguire</p>
                  <ul className="space-y-1">
                    {selectedOrder.work_description.map((desc, idx) => (
                      <li key={idx} className="text-sm text-zinc-300">• {desc}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedOrder.notes && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-1">Note</p>
                  <p className="text-zinc-300 text-sm">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Riprogramma Ordine #{selectedOrder?.work_order_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Nuova Data</label>
              <Input
                type="date"
                value={rescheduleData.scheduled_date}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                className="w-full"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Ora Inizio</label>
                <Input
                  type="time"
                  value={rescheduleData.scheduled_time_start}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, scheduled_time_start: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Ora Fine</label>
                <Input
                  type="time"
                  value={rescheduleData.scheduled_time_end}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, scheduled_time_end: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRescheduleDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleReschedule} className="btn-primary">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Assegna Ordine #{selectedOrder?.work_order_number}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Operatore</label>
              <Select 
                value={assignData.operator_id} 
                onValueChange={(val) => setAssignData(prev => ({ ...prev, operator_id: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona operatore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Non assegnato</SelectItem>
                  {settings.operators.map((op) => (
                    <SelectItem key={op.id} value={op.id}>
                      {op.name} {op.specialization && `(${op.specialization})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {settings.operators.length === 0 && (
                <p className="text-xs text-zinc-500 mt-1">
                  Nessun operatore configurato. Vai in Configurazioni per aggiungerli.
                </p>
              )}
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Ponte</label>
              <Select 
                value={String(assignData.bridge_number)} 
                onValueChange={(val) => setAssignData(prev => ({ ...prev, bridge_number: parseInt(val) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona ponte" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: settings.num_bridges }, (_, i) => i + 1).map((num) => (
                    <SelectItem key={num} value={String(num)}>
                      Ponte #{num}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAssign} className="btn-primary">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
